/**
 * Compact toolbar date picker (jump to date).
 *
 * A shadcn-styled NATIVE date input replaces the previous MUI x-date-picker:
 * same behaviour, but 32px tall like every other toolbar control, localized
 * by the browser (DD.MM.YYYY on German systems) with the platform's built-in
 * calendar popup — and without pulling popover/day-picker dependencies.
 */
import moment from "moment";
import { cn } from "@/lib/utils";

interface CalendarDatePickerProps {
  onChange: (date: string) => void;
  date: string;
}

const CalendarDatePicker = ({ date, onChange }: CalendarDatePickerProps) => {
  const value = moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : "";

  return (
    <input
      type="date"
      value={value}
      onChange={(e) => {
        // Ignore transient empty values while the user types/clears the field.
        if (e.target.value) onChange(e.target.value);
      }}
      aria-label="Datum wählen"
      className={cn(
        "h-8 w-[7.25rem] rounded-md border border-input bg-background px-1.5 text-sm text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        // The gap between "21.09.2026" and the calendar icon is not padding —
        // WebKit lays a date input out as [datetime-edit][indicator] with the
        // edit flexing to fill, so ANY width beyond the text opens up right
        // there. `input[type=date]` sizes itself generously by default, so the
        // only way to close the gap is to size the control to its content;
        // zeroing the padding below alone left it wide open.
        "[&::-webkit-datetime-edit]:p-0",
        "[&::-webkit-datetime-edit-fields-wrapper]:p-0",
        "[&::-webkit-inner-spin-button]:hidden",
        "[&::-webkit-calendar-picker-indicator]:m-0",
        "[&::-webkit-calendar-picker-indicator]:p-0",
        "[&::-webkit-calendar-picker-indicator]:cursor-pointer"
      )}
    />
  );
};

export default CalendarDatePicker;